"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { ArrowRight } from "lucide-react";

type Point = {
  time: string;
  value: number | null;
};

type IndexSeries = {
  key: string;
  label: string;
  first: number | null;
  latest: number | null;
  change: number | null;
  change_pct: number | null;
  high: number | null;
  low: number | null;
  points: Point[];
};

type HistoricalRangePayload = {
  kind: string;
  available: boolean;
  series: IndexSeries[];
};

type IndexFluctuationPayload = {
  as_of_time: string | null;
  range: { start: string; end: string };
  sample_count: number;
  series: IndexSeries[];
  historical_ranges?: Record<string, HistoricalRangePayload>;
};

const CHART_WIDTH = 980;
const CHART_HEIGHT = 280;
const PAD_LEFT = 8;
const PAD_RIGHT = 78;
const PAD_TOP = 16;
const PAD_BOTTOM = 28;
const PLOT_WIDTH = CHART_WIDTH - PAD_LEFT - PAD_RIGHT;
const PLOT_HEIGHT = CHART_HEIGHT - PAD_TOP - PAD_BOTTOM;
const RANGE_TABS = ["1D", "1M", "3M", "6M", "1Y"] as const;

const fmt = (value: number | null | undefined, digits = 2) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "-";
  return value.toLocaleString(undefined, { maximumFractionDigits: digits });
};

const fmtSigned = (value: number | null | undefined, digits = 2) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "-";
  return `${value > 0 ? "+" : ""}${fmt(value, digits)}`;
};

const fmtPct = (value: number | null | undefined) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "-";
  return `${value > 0 ? "+" : ""}${fmt(value, 2)}%`;
};

const numericPoints = (points: Point[]) =>
  points.filter((point): point is Point & { value: number } => typeof point.value === "number" && !Number.isNaN(point.value));

/** Counts from `from` to `target` with an ease-out curve - the number "replays the day"
 * alongside the chart draw-on. Skipped under prefers-reduced-motion. */
function useAnimatedValue(target: number | null | undefined, from: number | null | undefined, durationMs = 950) {
  const [display, setDisplay] = useState<number | null | undefined>(target);

  useEffect(() => {
    if (typeof target !== "number" || Number.isNaN(target)) {
      const raf = requestAnimationFrame(() => setDisplay(target));
      return () => cancelAnimationFrame(raf);
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      const raf = requestAnimationFrame(() => setDisplay(target));
      return () => cancelAnimationFrame(raf);
    }
    const start = typeof from === "number" && !Number.isNaN(from) ? from : target;
    const firstFrame = requestAnimationFrame(() => setDisplay(start));
    let raf = 0;
    const t0 = performance.now();
    const tick = (now: number) => {
      const progress = Math.min(1, (now - t0) / durationMs);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(start + (target - start) * eased);
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(raf);
    };
  }, [target, from, durationMs]);

  return display;
}

function makeChart(series: IndexSeries | null) {
  const points = numericPoints(series?.points ?? []);
  const values = points.map((point) => point.value);
  if (points.length < 2) return { linePath: "", areaPath: "", yTicks: [], xTicks: [] };

  const min = Math.min(...values);
  const max = Math.max(...values);
  const padding = Math.max((max - min) * 0.12, max * 0.001);
  const low = min - padding;
  const high = max + padding;
  const span = high - low || 1;
  const yFor = (value: number) => PAD_TOP + PLOT_HEIGHT - ((value - low) / span) * PLOT_HEIGHT;
  const xFor = (index: number) => PAD_LEFT + (index / Math.max(1, points.length - 1)) * PLOT_WIDTH;
  const coords = points.map((point, index) => ({ x: xFor(index), y: yFor(point.value), time: point.time }));
  const linePath = coords.map((point, index) => `${index === 0 ? "M" : "L"}${point.x.toFixed(2)},${point.y.toFixed(2)}`).join(" ");
  const baseline = PAD_TOP + PLOT_HEIGHT;
  const areaPath = `${linePath} L${coords[coords.length - 1].x.toFixed(2)},${baseline} L${coords[0].x.toFixed(2)},${baseline} Z`;
  const yTicks = Array.from({ length: 5 }, (_, index) => {
    const value = low + ((high - low) * index) / 4;
    return { value, y: yFor(value) };
  }).reverse();
  const xIndexes = [0, Math.floor(points.length * 0.25), Math.floor(points.length * 0.5), Math.floor(points.length * 0.75), points.length - 1];
  const xTicks = xIndexes.map((index) => ({ x: xFor(index), label: points[index]?.time ?? "" }));

  return { linePath, areaPath, yTicks, xTicks };
}

type HomeIndexFluctuationProps = {
  embedded?: boolean;
};

export default function HomeIndexFluctuation({ embedded = false }: HomeIndexFluctuationProps) {
  const [data, setData] = useState<IndexFluctuationPayload | null>(null);
  const [selectedKey, setSelectedKey] = useState("kse_100");
  const [activeRange, setActiveRange] = useState<(typeof RANGE_TABS)[number]>("1D");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    async function load() {
      try {
        setError(null);
        const response = await fetch("/api/indices/fluctuation?points=200", { cache: "no-store", signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        setData((await response.json()) as IndexFluctuationPayload);
      } catch (err) {
        if (!controller.signal.aborted) setError(err instanceof Error ? err.message : "Unable to load index feed");
      }
    }

    void load();
    return () => controller.abort();
  }, []);

  const activeSeriesList = activeRange === "1D" ? data?.series ?? [] : data?.historical_ranges?.[activeRange]?.series ?? [];
  const selectedSeries = activeSeriesList.find((row) => row.key === selectedKey) ?? activeSeriesList[0] ?? null;
  const chart = useMemo(() => makeChart(selectedSeries), [selectedSeries]);
  const toneClass = selectedSeries?.change && selectedSeries.change > 0 ? "is-positive" : selectedSeries?.change && selectedSeries.change < 0 ? "is-negative" : "is-neutral";
  const activePointCount = activeRange === "1D" ? data?.sample_count ?? 0 : selectedSeries?.points.length ?? 0;
  const chartKey = `${selectedSeries?.key ?? "none"}-${activeRange}`;
  const animatedLatest = useAnimatedValue(selectedSeries?.latest, selectedSeries?.first);

  const card = (
        <div className="homeIndexCard">
          <div className="homeSessionSectionHead homeIndexHead">
            <h2 id="home-index-title">Index Fluctuation</h2>
            <Link href="/indices/fluctuation">
              Open full chart
              <ArrowRight size={16} strokeWidth={2.2} aria-hidden="true" />
            </Link>
          </div>

          <div className="homeIndexSummary">
            <div>
              <span>{selectedSeries?.label ?? "KSE-100"}</span>
              <strong>{fmt(animatedLatest)}</strong>
            </div>
            <em className={toneClass}>
              {fmtSigned(selectedSeries?.change)} pts ({fmtPct(selectedSeries?.change_pct)})
            </em>
          </div>

          <div className="homeIndexControls">
            <div className="homeIndexTabs" aria-label="Select index">
              {(data?.series ?? []).map((row) => (
                <button
                  key={row.key}
                  type="button"
                  className={row.key === selectedSeries?.key ? "is-active" : undefined}
                  onClick={() => setSelectedKey(row.key)}
                >
                  {row.label}
                </button>
              ))}
            </div>
            <div className="homeIndexRanges" aria-label="Select range">
              {RANGE_TABS.map((range) => (
                <button
                  key={range}
                  type="button"
                  className={range === activeRange ? "is-active" : undefined}
                  disabled={range !== "1D" && !data?.historical_ranges?.[range]?.available}
                  onClick={() => setActiveRange(range)}
                >
                  {range}
                </button>
              ))}
            </div>
          </div>

          <div className="homeIndexChartFrame">
            {error ? (
              <div className="homeIndexState">Index feed unavailable: {error}</div>
            ) : !data ? (
              <div className="homeIndexState">Loading index performance...</div>
            ) : (
              <svg key={chartKey} className="homeIndexChart" viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`} role="img" aria-label={`${selectedSeries?.label ?? "Index"} ${activeRange} chart`}>
                <defs>
                  <linearGradient id="home-index-area-fill" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#f2b844" stopOpacity="0.34" />
                    <stop offset="100%" stopColor="#f2b844" stopOpacity="0.12" />
                  </linearGradient>
                </defs>
                <rect x={PAD_LEFT} y={PAD_TOP} width={PLOT_WIDTH} height={PLOT_HEIGHT} className="homeIndexPlotBg" />
                {chart.yTicks.map((tick) => (
                  <g key={tick.y}>
                    <line x1={PAD_LEFT} x2={PAD_LEFT + PLOT_WIDTH} y1={tick.y} y2={tick.y} className="homeIndexGridLine" />
                    <text x={PAD_LEFT + PLOT_WIDTH + 12} y={tick.y + 4} className="homeIndexAxisText">
                      {fmt(tick.value, 0)}
                    </text>
                  </g>
                ))}
                {chart.xTicks.map((tick) => (
                  <g key={`${tick.x}-${tick.label}`}>
                    <line x1={tick.x} x2={tick.x} y1={PAD_TOP} y2={PAD_TOP + PLOT_HEIGHT} className="homeIndexGridLine" />
                    <text x={tick.x} y={CHART_HEIGHT - 7} className="homeIndexXAxisText">
                      {tick.label}
                    </text>
                  </g>
                ))}
                {chart.areaPath ? <path d={chart.areaPath} className="homeIndexAreaFill is-drawn" /> : null}
                {chart.linePath ? <path d={chart.linePath} pathLength={1} className="homeIndexAreaLine is-drawn" /> : null}
              </svg>
            )}
          </div>

          <div className="homeIndexMeta">
            <span>{activeRange === "1D" ? "Official PSX intraday feed" : "PSX historical workbook proxy"}</span>
            <span>{data ? `${activePointCount.toLocaleString()} points` : "Waiting for feed"}</span>
          </div>
        </div>
  );

  if (embedded) {
    return (
      <div className="homeIndexBand homeIndexBand--embedded" aria-labelledby="home-index-title">
        {card}
      </div>
    );
  }

  return (
    <section className="homeIndexBand" aria-labelledby="home-index-title">
      <div className="homeSessionShell">{card}</div>
    </section>
  );
}
